import weibo from './weibo.png';
import twitter from './twitter.png';
import tumblr from './tumblr.png';
import linkedin from './linkedin.png';
import instagram from './instagram.png';
import facebook from './facebook.png';
import './App.css';
import '../node_modules/bootstrap/dist/css/bootstrap.min.css';
import { Nav, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import chart_hosting from "./chart_hosting.json";
import chart_front from "./chart_front.json";
import chart_back from "./chart_back.json";
import chart_cms from "./chart_cms.json";
import chart_mobile from "./chart_mobile.json";
import chart_os from "./chart_os.json";
import chart_ai from "./chart_ai.json";
import bookTopics from "./book-topics.json";
import siteStats from "./site-stats.json";
import { useState, useRef, useEffect, useMemo } from "react";
import QATree from "./tree_QA";
import TechnologyTree from "./tree_technology";
import ReactFBLike from 'react-fb-like';

// One menu definition feeds the desktop menu bar, the phone menu and the topic search.
const topics = (predicate) => bookTopics.filter(predicate).map(topic => ({ href: topic.url, title: topic.title }));
const DIVIDER = { divider: true };
const menuGroups = [
  { title: "Web Hosting", items: [
    { href: "/development-environments/development-environments.html", title: "Development Environments" },
    { href: "/aws/aws.html", title: "AWS" },
    { href: "/aws-certification/aws-certification.html", title: "AWS Certification" },
    { href: "#cms", title: "CMS" }] },
  { title: "Front-End Web Development", short: "Front-End", items: [
    ...topics(topic => topic.book === "front-end" && topic.topic !== "development-environments"),
    { href: "/less/less.html", title: "LESS" }, { href: "/sass/sass.html", title: "SASS" }, DIVIDER,
    { href: "/jquery/jquery.html", title: "JQuery" }, { href: "/react/react.html", title: "React" },
    { href: "/redux/redux.html", title: "Redux" }, { href: "/extensions-and-tools/extensions-and-tools.html", title: "Extensions and Tools" }] },
  { title: "Back-End Web Development", short: "Back-End", items: [
    ...topics(topic => topic.book === "lamp" || (topic.book === "mern" && topic.topic !== "react")),
    { href: "/xml/xml.html", title: "XML" }] },
  { title: "Mobile App Development", short: "Mobile", items: topics(topic => topic.book === "mobile") },
  { title: "DevOps", items: topics(topic => topic.book === "devops") },
  { title: "Web Graphics", short: "Graphics", items: topics(topic => topic.book === "graphics") },
];

// The six domains (one per book, in the series' volume order), plus the hand-written topics.
const books = [
  { key: "front-end", title: "Front-End Web Development", icon: "🎨", tone: "indigo",
    blurb: "Structure, style and behaviour in the browser: HTML, CSS, Bootstrap, Tailwind, modern JavaScript, TypeScript, the DOM, Web APIs and SEO." },
  { key: "mern", title: "MERN Stack Development", icon: "⚛️", tone: "teal",
    blurb: "Full-stack JavaScript: Node.js, Express, MongoDB, React and Next.js, from the first server to a deployed application." },
  { key: "lamp", title: "LAMP Stack Development", icon: "🐘", tone: "amber",
    blurb: "The classic web stack: Linux, Apache, MySQL, PHP 8.5 and Laravel, configured, secured and tuned." },
  { key: "mobile", title: "Mobile App Development", icon: "📱", tone: "rose",
    blurb: "Apps for phones and tablets with Cordova, Framework7, React Native, Capacitor and native Android in Kotlin." },
  { key: "devops", title: "Package Managers and DevOps", icon: "🚀", tone: "sky",
    blurb: "Shipping software: npm, pnpm and Yarn, Git and GitHub, Docker, Jenkins, Kubernetes and CloudFormation." },
  { key: "graphics", title: "Web Graphics", icon: "🧊", tone: "violet",
    blurb: "Drawing on the web: SVG, Canvas, WebGL, WebGPU, Three.js, D3, Babylon.js and AI image generation." },
];
const moreTopics = [
  { href: "/xml/xml.html", title: "XML" }, { href: "/jquery/jquery.html", title: "jQuery" },
  { href: "/less/less.html", title: "Less" }, { href: "/sass/sass.html", title: "Sass" },
  { href: "/redux/redux.html", title: "Redux" }, { href: "/aws/aws.html", title: "AWS" },
  { href: "/extensions-and-tools/extensions-and-tools.html", title: "Extensions and Tools" },
];
const searchable = [
  ...bookTopics.map(topic => ({ href: topic.url, title: topic.title, book: books.find(book => book.key === topic.book)?.title || "Web Hosting" })),
  ...moreTopics.map(topic => ({ ...topic, book: "More topics" })),
];

const features = [
  { icon: "▶", title: "Edit and run every example", text: "Code listings are live editors: change the code and the output or preview updates as you type, right in the page." },
  { icon: "◈", title: "See it, don't just read it", text: "Diagrams, figures, printed outputs and a runnable graphic on every Web Graphics page show what the code does." },
  { icon: "✦", title: "Current to 2026", text: "ECMAScript 2026, PHP 8.5, React 19, Kotlin, Kubernetes, WebGPU and more, with links to each tool's own site." },
  { icon: "☰", title: "Structured for learning", text: "Topics, sections and short, focused pages, with breadcrumbs, previous/next links and cross-references." },
];

// Topic-landscape tabs: explanation plus the interactive tree diagram.
const landscape = [
  { id: "hosting", title: "Web Hosting & CMS", chart: chart_hosting, tree: QATree, extra: { id: "cms", chart: chart_cms },
    text: <>
      <p>To host a website with a domain name you desire (such as <i>mydomain.com</i>), first decide where the server's files will live: on your own premises, or on a remote server rented in the cloud.</p>
      <p>There are three levels of cloud computing:</p>
      <ul>
        <li><b>IaaS</b>, Infrastructure-as-a-Service: you manage the application and the operating system (AWS EC2, Microsoft Azure, Google Compute Engine).</li>
        <li><b>PaaS</b>, Platform-as-a-Service: you manage the application only (AWS Elastic Beanstalk, Azure App Service, Google App Engine).</li>
        <li><b>SaaS</b>, Software-as-a-Service: you only use the application (wordpress.com, Google Drive, Zoom, Dropbox).</li>
      </ul>
    </>,
    extraText: <>
      <h3 id="cms">Content Management Systems</h3>
      <p>A CMS is a website builder for creating and maintaining SEO-friendly, mobile-friendly sites in the browser, without coding the front end and back end. <b>WordPress</b>, with its huge plugin ecosystem, can be used hosted (wordpress.com or a web host) or self-hosted (wordpress.org). <b>Wix</b> offers a simpler drag-and-drop editor.</p>
    </> },
  { id: "front", title: "Front-End", chart: chart_front, tree: TechnologyTree,
    text: <>
      <p>Front-end development is about how people experience a website in the browser, a science as much as an art.</p>
      <p><b>HTML</b> defines a page's structure, <b>CSS</b> how it looks and adapts to any screen (with frameworks such as <b>Bootstrap</b> and <b>Tailwind</b>), and <b>JavaScript</b> makes it interactive. <b>React</b> organises the interface into reusable components, and <b>SEO</b> helps people find it.</p>
    </> },
  { id: "back", title: "Back-End", chart: chart_back, tree: TechnologyTree,
    text: <>
      <p>Back-end development deals with servers: web servers store and serve pages and assets, while database servers store and process users' data.</p>
      <p><b>LAMP</b> (Linux, Apache, MySQL, PHP) and <b>MERN</b> (MongoDB, Express, React, Node.js) are two popular stacks. A developer who does both front end and back end is a full-stack developer.</p>
    </> },
  { id: "mobile", title: "Mobile", chart: chart_mobile, tree: TechnologyTree,
    text: <>
      <p>Mobile apps run on phones and tablets, work offline and are distributed through app stores.</p>
      <p><b>Native</b> development targets one platform with its own tools (Android Studio and Kotlin, Xcode and Swift). <b>Hybrid</b> and cross-platform development targets several platforms from one codebase, usually with JavaScript, HTML and CSS plus plugins for device features.</p>
    </> },
  { id: "os", title: "OS & Cloud", chart: chart_os, tree: TechnologyTree,
    text: <>
      <p>The three major operating systems are <b>Windows</b>, <b>Linux</b> (free and open source) and <b>macOS</b>; <b>C</b> is the natural language for programming them.</p>
      <p>Cloud providers such as <b>AWS</b>, <b>Microsoft Azure</b> and <b>Google Cloud</b> rent out servers, and <b>Docker</b> packages applications into containers that run the same in any environment.</p>
    </> },
  { id: "ai", title: "AI", chart: chart_ai, tree: TechnologyTree,
    text: <>
      <p><b>Machine learning</b>, a part of artificial intelligence, studies algorithms that improve through experience and data. In supervised learning, a model is trained on inputs with known outputs, then predicts outputs for new inputs.</p>
      <p>Estimators that predict discrete classes are <b>classifiers</b>; those that predict continuous values are <b>regressors</b>. <a href="https://www.kaggle.com/" target="_blank" rel="noopener noreferrer">Kaggle</a> hosts datasets and competitions for practitioners.</p>
    </> },
];
// "2026-09-29" -> "29 Sep 2026" (fixed English month names, independent of the browser locale).
const formatDate = iso => {
  const [year, month, day] = iso.split('-').map(Number);
  return day + ' ' + ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month - 1] + ' ' + year;
};
const tabForHash = hash => {
  const id = hash.replace('#', '');
  if (id === 'cms') return 'hosting';
  return landscape.some(tab => tab.id === id) ? id : null;
};

/** Phones and narrow windows: a floating "Topics" button opens the same menus as a drop-down list. */
function PhoneMenu() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = event => { if (event.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);
  return (
    <div className="phone-menu">
      {open && <div id="phone-menu-panel" className="phone-menu-panel" role="navigation" aria-label="Topics">
        {menuGroups.map(group =>
          <details key={group.title}>
            <summary>{group.title}</summary>
            <ul>
              {group.items.filter(item => !item.divider).map(item =>
                <li key={item.href}><a href={item.href} onClick={() => setOpen(false)}>{item.title}</a></li>)}
            </ul>
          </details>)}
      </div>}
      <button type="button" className="phone-menu-button" aria-expanded={open} aria-controls="phone-menu-panel"
        onClick={() => setOpen(!open)}>{open ? "✕ Close" : "☰ Topics"}</button>
    </div>
  );
}

/** Type to find a topic; Enter opens the first match. */
function TopicSearch() {
  const [query, setQuery] = useState("");
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return searchable.filter(topic => topic.title.toLowerCase().includes(q) || (topic.book || "").toLowerCase().includes(q)).slice(0, 8);
  }, [query]);
  return (
    <form className="topic-search" role="search" onSubmit={event => { event.preventDefault(); if (results[0]) window.location.href = results[0].href; }}>
      <label htmlFor="topic-search-input" className="visually-hidden">Find a topic</label>
      <input id="topic-search-input" type="search" placeholder="Find a topic: React, SQL, Docker, WebGPU…" autoComplete="off"
        value={query} onChange={event => setQuery(event.target.value)} aria-controls="topic-search-results" />
      {query.trim() && <ul id="topic-search-results" className="topic-search-results">
        {results.length ? results.map(topic =>
          <li key={topic.href}><a href={topic.href}><strong>{topic.title}</strong><span>{topic.book}</span></a></li>)
          : <li className="topic-search-empty">No topic matches “{query}”.</li>}
      </ul>}
    </form>
  );
}

/**
 * Renders a tree diagram, crops the SVG to its drawn content (no empty margins) and lets it
 * scale to the container: large trees shrink to fit, small ones grow a little. Redrawn on resize.
 */
function Diagram({ data, tree, title }) {
  const box = useRef(null);
  useEffect(() => {
    const draw = () => {
      const container = box.current;
      if (!container) return;
      const width = Math.max(320, container.clientWidth);
      // The question tree needs more room between its answers than the container offers.
      const isQuestions = tree === QATree;
      const renderWidth = isQuestions ? Math.max(1800, width) : width;
      const svg = tree(data, {
        label: d => d.name, title: d => d.title, link: d => d.link,
        width: renderWidth, height: isQuestions ? renderWidth * 0.8 : Math.max(420, width * 0.7),
      });
      container.replaceChildren(svg);
      const content = svg.getBBox();
      const pad = 16;
      svg.setAttribute('viewBox', [content.x - pad, content.y - pad, content.width + 2 * pad, content.height + 2 * pad].join(' '));
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.style.width = '100%';
      svg.style.height = 'auto';
      svg.style.maxWidth = Math.round((content.width + 2 * pad) * (isQuestions ? 1 : 1.15)) + 'px';
      svg.style.margin = '0 auto';
      svg.style.border = 'none';
    };
    draw();
    window.addEventListener('resize', draw);
    return () => window.removeEventListener('resize', draw);
  }, [data, tree]);
  return <div className="diagram" ref={box} role="img" aria-label={title + " technology map"}></div>;
}

function App() {
  const [tab, setTab] = useState(() => tabForHash(window.location.hash) || 'hosting');
  // Menu links such as "#cms" or "#front" open the matching tab and scroll to it.
  useEffect(() => {
    const follow = () => {
      const next = tabForHash(window.location.hash);
      if (!next) return;
      setTab(next);
      requestAnimationFrame(() => document.getElementById(window.location.hash.slice(1) === 'cms' ? 'cms' : 'landscape')?.scrollIntoView({ behavior: 'smooth' }));
    };
    follow();
    window.addEventListener('hashchange', follow);
    return () => window.removeEventListener('hashchange', follow);
  }, []);
  const active = landscape.find(item => item.id === tab);
  const fmt = n => n.toLocaleString('en-US');

  return (
    <div className="home">
      <header className="site-header">
        <a className="brand" href="/index.html"><span className="brand-mark" aria-hidden="true">&lt;/&gt;</span>Web Coding Center</a>
        <Nav className="desktop-menu" navbar>
          {menuGroups.map(group =>
            <UncontrolledDropdown key={group.title} inNavbar nav>
              <DropdownToggle caret nav>
                <span className="label-full">{group.title}</span><span className="label-short">{group.short || group.title}</span>
              </DropdownToggle>
              <DropdownMenu end>
                {group.items.map((item, i) => item.divider
                  ? <DropdownItem key={"divider" + i} divider />
                  : <DropdownItem key={item.href} href={item.href}>{item.title}</DropdownItem>)}
              </DropdownMenu>
            </UncontrolledDropdown>)}
        </Nav>
      </header>
      {/* Outside the header: its backdrop-filter would make "position: fixed" relative to the header, not the screen. */}
      <PhoneMenu />

      <main>
        <section className="hero">
          <div className="hero-inner">
            <p className="hero-kicker">Master the art of computing</p>
            <h1>Learn web development by running the code, not just reading it.</h1>
            <p className="hero-lead">Free tutorials across 6 domains, from HTML and CSS to React, PHP, Kotlin, Docker and WebGPU,
              with editable examples that show their results as you type.</p>
            <TopicSearch />
            <div className="hero-actions">
              <a className="button primary" href="/html/html.html">Start with HTML</a>
              <a className="button" href="#domains">Explore the domains</a>
            </div>
            <dl className="stats">
              <div><dt>Domains</dt><dd>{siteStats.books}</dd></div>
              <div><dt>Topics</dt><dd>{siteStats.chapters + moreTopics.length}</dd></div>
              <div><dt>Pages</dt><dd>{fmt(siteStats.pages)}+</dd></div>
              <div><dt>Code examples</dt><dd>{fmt(siteStats.examples)}+</dd></div>
            </dl>
          </div>
        </section>

        <section id="domains" className="section">
          <div className="section-head">
            <h2>Learning paths</h2>
            <p>Each domain is a path through one area of web development. Pick a domain, then a topic.</p>
          </div>
          <div className="book-grid">
            {books.map(book => {
              const chapters = bookTopics.filter(topic => topic.book === book.key);
              return (
                <article key={book.key} className={"book-card tone-" + book.tone}>
                  <div className="book-card-head">
                    <span className="book-icon" aria-hidden="true">{book.icon}</span>
                    <div>
                      <h3>{book.title}</h3>
                      <span className="book-meta">{chapters.length} topics</span>
                    </div>
                  </div>
                  <p>{book.blurb}</p>
                  <ul className="chips">
                    {chapters.map(topic => <li key={topic.topic}><a href={topic.url}>{topic.title}</a></li>)}
                  </ul>
                  {chapters[0] && <a className="book-start" href={chapters[0].url}>Start here: {chapters[0].title} →</a>}
                </article>);
            })}
            <article className="book-card tone-slate">
              <div className="book-card-head">
                <span className="book-icon" aria-hidden="true">📚</span>
                <div><h3>More topics</h3><span className="book-meta">{moreTopics.length} topics</span></div>
              </div>
              <p>Hand-written tutorials on XML, jQuery, CSS preprocessors, Redux, AWS and developer tools.</p>
              <ul className="chips">
                {moreTopics.map(topic => <li key={topic.href}><a href={topic.href}>{topic.title}</a></li>)}
              </ul>
            </article>
          </div>
        </section>

        <section className="section features-section">
          <div className="section-head"><h2>Why learn here</h2></div>
          <div className="features">
            {features.map(feature =>
              <div key={feature.title} className="feature">
                <span className="feature-icon" aria-hidden="true">{feature.icon}</span>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </div>)}
          </div>
        </section>

        <section id="landscape" className="section">
          <div className="section-head">
            <h2>The technology landscape</h2>
            <p>How the pieces fit together. Hover over a technology for details; click it to open its tutorial.</p>
          </div>
          <div className="tabs" role="tablist" aria-label="Technology areas">
            {landscape.map(item =>
              <button key={item.id} id={"tab-" + item.id} role="tab" type="button" aria-selected={item.id === tab} aria-controls="landscape-panel"
                className={item.id === tab ? "tab active" : "tab"} onClick={() => { setTab(item.id); window.history.replaceState(null, '', '#' + item.id); }}>
                {item.title}
              </button>)}
          </div>
          <div id="landscape-panel" className="tab-panel" role="tabpanel" aria-labelledby={"tab-" + tab}>
            <div className="tab-text">{active.text}</div>
            <Diagram key={active.id} data={active.chart} tree={active.tree} title={active.title} />
            {active.extra && <>
              <div className="tab-text">{active.extraText}</div>
              <Diagram key={active.extra.id} data={active.extra.chart} tree={TechnologyTree} title="Content management systems" />
            </>}
          </div>
        </section>
      </main>

      <footer className="site-footer">
        {/* One line when it fits; the items wrap onto more lines on narrow screens. */}
        <div className="footer-line">
          <a href="mailto:webcodingbible@gmail.com">webcodingbible@gmail.com</a>
          <span>Last Update: <time dateTime={siteStats.updated}>{formatDate(siteStats.updated)}</time></span>
          <span>© All rights reserved.</span>
        </div>
        <div className="footer-social">
          <a target="_blank" rel="noopener noreferrer" href="https://www.facebook.com/webcodingcenter"><img src={facebook} alt="Facebook" /></a>
          <a target="_blank" rel="noopener noreferrer" href="https://twitter.com/webcodingcenter"><img src={twitter} alt="Twitter" /></a>
          <a target="_blank" rel="noopener noreferrer" href="https://www.linkedin.com/in/web-coding-center-8a1353226/detail/recent-activity/"><img src={linkedin} alt="LinkedIn" /></a>
          <a target="_blank" rel="noopener noreferrer" href="https://www.instagram.com/webcodingcenter/"><img src={instagram} alt="Instagram" /></a>
          <a target="_blank" rel="noopener noreferrer" href="https://www.tumblr.com/blog/web-coding-center"><img src={tumblr} alt="Tumblr" /></a>
          <a target="_blank" rel="noopener noreferrer" href="https://weibo.com/u/7724791178"><img src={weibo} alt="Weibo" /></a>
          <ReactFBLike appId="1399772917172608" version="v12.0" />
        </div>
      </footer>
    </div>
  );
}

export default App;
